import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import cookies from 'js-cookie'

type PointsState = {
  value: number
  formatted: string
}

type LoyaltyCounter = {
  value?: number
  type?: {
    value?: string
  }
}

type LoyaltyAccount = {
  id?: string
  legacyId?: string
  customer?: string | number
  type?: number | string
  counters?: LoyaltyCounter[]
}

type UseLysLoyaltyAccountParams = {
  mdmClientNumber?: number | string
  lysHomepageEnabled: boolean
}

type UseLysLoyaltyAccountReturn = {
  isLysUser: boolean
  lysProgramType: number | null
  mdmPoints: PointsState | null
  isLoadingMdmPoints: boolean
  refreshLysAccount: () => Promise<void>
}

const POINTS_UPDATED_EVENT = 'lys:points-updated'

const formatPoints = (value: number) => Intl.NumberFormat('pt-BR').format(value)

export const useLysLoyaltyAccount = ({
  mdmClientNumber,
  lysHomepageEnabled
}: UseLysLoyaltyAccountParams): UseLysLoyaltyAccountReturn => {
  const [isLysUser, setIsLysUser] = useState(false)
  const [lysProgramType, setLysProgramType] = useState<number | null>(null)
  const [mdmPoints, setMdmPoints] = useState<PointsState | null>(null)
  const [isLoadingMdmPoints, setIsLoadingMdmPoints] = useState(false)

  const resetLysState = useCallback(() => {
    setMdmPoints(null)
    setIsLysUser(false)
    setLysProgramType(null)
  }, [])

  const fetchLoyaltyAccount = useCallback(async () => {
    if (!mdmClientNumber || !lysHomepageEnabled) {
      resetLysState()
      return
    }

    setIsLoadingMdmPoints(true)

    try {
      const token = cookies.get('api_token')

      if (!token) {
        resetLysState()
        return
      }

      const res = await axios.get(
        '/api/v2/loyaltyProgram/lys/loyalty-accounts',
        {
          params: {
            customer: mdmClientNumber
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      const payload = res?.data

      const account: LoyaltyAccount = payload?.data?.data

      const hasLysAccount =
        Boolean(account.id) || account.customer != null || account.type != null

      setIsLysUser(hasLysAccount)

      const parsedType = Number.parseInt(String(account.type ?? ''), 10)

      setLysProgramType(Number.isFinite(parsedType) ? parsedType : null)

      const counters: LoyaltyCounter[] = Array.isArray(account.counters)
        ? account.counters
        : []

      const pointsCounter = counters.find(
        counter => counter.type?.value === 'P'
      )

      const value = Number(pointsCounter?.value ?? 0)

      setMdmPoints({
        value,
        formatted: formatPoints(value)
      })
    } catch {
      resetLysState()
    } finally {
      setIsLoadingMdmPoints(false)
    }
  }, [mdmClientNumber, resetLysState, lysHomepageEnabled])

  useEffect(() => {
    if (!lysHomepageEnabled) {
      return
    }

    if (!mdmClientNumber) {
      resetLysState()

      return
    }

    fetchLoyaltyAccount()
  }, [mdmClientNumber, fetchLoyaltyAccount, resetLysState, lysHomepageEnabled])

  useEffect(() => {
    if (!mdmClientNumber && !lysHomepageEnabled) {
      return
    }

    const onPointsUpdated = () => {
      fetchLoyaltyAccount()
    }

    const onFocus = () => {
      fetchLoyaltyAccount()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchLoyaltyAccount()
      }
    }

    window.addEventListener(POINTS_UPDATED_EVENT, onPointsUpdated)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener(POINTS_UPDATED_EVENT, onPointsUpdated)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [mdmClientNumber, fetchLoyaltyAccount, lysHomepageEnabled])

  return {
    isLysUser,
    lysProgramType,
    mdmPoints,
    isLoadingMdmPoints,
    refreshLysAccount: fetchLoyaltyAccount
  }
}
